import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Tooltip from '@mui/joy/Tooltip'
import IconButton from '@mui/joy/IconButton'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { CircularProgress } from '@mui/joy'
import axios from 'axios'
import { formatToUserLocalTime } from '@/utils/date-formatting'
import { NotificationLogEntry } from '@numeo/types'

interface NotificationLogRow extends NotificationLogEntry {
    updatedAt?: string
    durationMs?: number
    responseCode?: number
    deliveryId?: string
    truckId?: string
    sourceRateConfId?: string
    recipientTypes?: string
    senderEmail?: string
    driverName?: string
}

const PAGE_SIZE = 50

const statusColors: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'primary'> = {
    sent: 'success',
    pending: 'warning',
    failed: 'danger',
    skipped: 'neutral',
}

const typeColors: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'primary'> = {
    info: 'primary',
    warning: 'warning',
    error: 'danger',
    success: 'success',
}

export default function NotificationLogs() {
    const [rows, setRows] = React.useState<NotificationLogRow[]>([])
    const [page, setPage] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [totalCount, setTotalCount] = React.useState(0)
    const [totalPages, setTotalPages] = React.useState(0)

    // Filter states
    const [searchFilter, setSearchFilter] = React.useState('')
    const [statusFilter, setStatusFilter] = React.useState('')
    const [channelFilter, setChannelFilter] = React.useState('')
    const [typeFilter, setTypeFilter] = React.useState('')
    const [loadIdFilter, setLoadIdFilter] = React.useState('')
    const [projectNameFilter, setProjectNameFilter] = React.useState('')
    const [dateFromFilter, setDateFromFilter] = React.useState('')
    const [dateToFilter, setDateToFilter] = React.useState('')

    // Applied filters (for triggering API calls)
    const [appliedSearchFilter, setAppliedSearchFilter] = React.useState('')
    const [appliedStatusFilter, setAppliedStatusFilter] = React.useState('')
    const [appliedChannelFilter, setAppliedChannelFilter] = React.useState('')
    const [appliedTypeFilter, setAppliedTypeFilter] = React.useState('')
    const [appliedLoadIdFilter, setAppliedLoadIdFilter] = React.useState('')
    const [appliedProjectNameFilter, setAppliedProjectNameFilter] = React.useState('')
    const [appliedDateFromFilter, setAppliedDateFromFilter] = React.useState('')
    const [appliedDateToFilter, setAppliedDateToFilter] = React.useState('')

    // Detail modal
    const [selectedLog, setSelectedLog] = React.useState<NotificationLogRow | null>(null)
    const [detailModalOpen, setDetailModalOpen] = React.useState(false)

    const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyFilters()
        }
    }

    const applyFilters = () => {
        setPage(1)
        setAppliedSearchFilter(searchFilter)
        setAppliedStatusFilter(statusFilter)
        setAppliedChannelFilter(channelFilter)
        setAppliedTypeFilter(typeFilter)
        setAppliedLoadIdFilter(loadIdFilter)
        setAppliedProjectNameFilter(projectNameFilter)
        setAppliedDateFromFilter(dateFromFilter)
        setAppliedDateToFilter(dateToFilter)
    }

    const clearFilters = () => {
        setSearchFilter('')
        setStatusFilter('')
        setChannelFilter('')
        setTypeFilter('')
        setLoadIdFilter('')
        setProjectNameFilter('')
        setDateFromFilter('')
        setDateToFilter('')
        setAppliedSearchFilter('')
        setAppliedStatusFilter('')
        setAppliedChannelFilter('')
        setAppliedTypeFilter('')
        setAppliedLoadIdFilter('')
        setAppliedProjectNameFilter('')
        setAppliedDateFromFilter('')
        setAppliedDateToFilter('')
        setPage(1)
    }

    const openDetailModal = (log: NotificationLogRow) => {
        setSelectedLog(log)
        setDetailModalOpen(true)
    }

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const isInitialLoad =
                    page === 1 &&
                    !appliedSearchFilter &&
                    !appliedStatusFilter &&
                    !appliedChannelFilter &&
                    !appliedTypeFilter &&
                    !appliedLoadIdFilter &&
                    !appliedProjectNameFilter &&
                    !appliedDateFromFilter &&
                    !appliedDateToFilter

                if (isInitialLoad) {
                    setLoading(true)
                } else {
                    setFetching(true)
                }

                const params = new URLSearchParams()
                params.append('page', page.toString())
                params.append('pageSize', PAGE_SIZE.toString())
                if (appliedSearchFilter) params.append('search', appliedSearchFilter)
                if (appliedStatusFilter) params.append('status', appliedStatusFilter)
                if (appliedChannelFilter) params.append('channel', appliedChannelFilter)
                if (appliedTypeFilter) params.append('notificationType', appliedTypeFilter)
                if (appliedLoadIdFilter) params.append('loadId', appliedLoadIdFilter)
                if (appliedProjectNameFilter) params.append('projectName', appliedProjectNameFilter)
                if (appliedDateFromFilter) params.append('dateFrom', appliedDateFromFilter)
                if (appliedDateToFilter) params.append('dateTo', appliedDateToFilter)

                const res = await axios.get(`/updater/notification-logs/paginated?${params.toString()}`)
                const data = res.data || {}

                // Process rows to extract fields from meta and handle nulls
                const processedRows = Array.isArray(data.rows)
                    ? data.rows.map((row: any) => {
                          // Extract fields from meta JSON string
                          let metaObj: any = {}
                          if (row.meta) {
                              try {
                                  metaObj = typeof row.meta === 'string' ? JSON.parse(row.meta) : row.meta
                              } catch (error) {
                                  metaObj = {}
                              }
                          }

                          // Helper to get value or undefined (so it falls back to the next || option)
                          const val = <T,>(v: T | null | undefined | '' | 'null' | 'undefined'): T | undefined => {
                              if (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined') {
                                  return undefined
                              }
                              return v as T
                          }

                          // Normalizes BigQuery timestamps if they come back as objects
                          const normalizeDate = (d: unknown): string | undefined => {
                              const v = val(d)
                              if (!v) return undefined
                              if (typeof v === 'string') return v
                              if (v && typeof v === 'object' && 'value' in v && (v as { value: unknown }).value !== undefined) {
                                  return String((v as { value: unknown }).value)
                              }
                              return String(v)
                          }

                          // Format notification type nicely
                          const formatNotificationType = (type: string) => {
                              const t = val(type) || val(metaObj.notificationType) || 'unknown'
                              if (t === 'unknown') return 'Unknown'
                              return String(t)
                                  .replace(/-/g, ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())
                          }

                          return {
                              ...row,
                              createdAt: normalizeDate(row.createdAt),
                              notificationType: formatNotificationType(row.notificationType),
                              truckId: val(metaObj.truckId) || val(row.truckId),
                              sourceRateConfId: val(metaObj.sourceRateConfId) || val(row.sourceRateConfId),
                              subject: val(metaObj.subject) || val(row.subject),
                              errorMessage: val(metaObj.errorMessage) || val(row.errorMessage),
                              errorStack: val(metaObj.errorStack) || val(row.errorStack),
                              senderEmail: val(row.senderEmail) || val(metaObj.senderEmail),
                              driverName: val(row.driverName) || val(metaObj.driverName),
                              loadId: val(row.loadId) || val(metaObj.loadId),
                              status: val(row.status) || val(metaObj.status),
                              channel: val(row.channel) || val(metaObj.channel),
                              recipientEmail: val(row.recipientEmail) || val(metaObj.recipientEmail),
                              templateName: val(row.templateName) || val(metaObj.templateName),
                              projectName: val(row.projectName) || val(metaObj.projectName) || 'N/A',
                          }
                      })
                    : []

                setRows(processedRows)
                setTotalCount(typeof data.total === 'number' ? data.total : 0)
                setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
            } catch (e) {
                console.error('Failed to fetch notification logs:', e)
                setRows([])
                setTotalCount(0)
                setTotalPages(0)
            } finally {
                setLoading(false)
                setFetching(false)
            }
        }

        fetchData()
    }, [
        page,
        appliedSearchFilter,
        appliedStatusFilter,
        appliedChannelFilter,
        appliedTypeFilter,
        appliedLoadIdFilter,
        appliedProjectNameFilter,
        appliedDateFromFilter,
        appliedDateToFilter,
    ])

    // Clamp page when totalPages shrinks
    React.useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(1)
        }
    }, [totalPages, page])

    const truncateMessage = (message: string, maxLength = 80) => {
        if (message.length <= maxLength) return message
        return message.substring(0, maxLength) + '...'
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading notification logs...</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography level="h2" component="h1">
                        Notification Logs ({totalCount})
                    </Typography>
                    {fetching && <CircularProgress size="sm" />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<RefreshIcon />}
                        disabled={fetching}
                        onClick={() => {
                            setPage(1)
                            setAppliedSearchFilter(searchFilter)
                        }}
                    >
                        Refresh
                    </Button>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages || 1}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page >= totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Box
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        width: '100%',
                        alignItems: 'flex-end',
                    }}
                >
                    <FormControl sx={{ flex: 1.5 }} size="sm">
                        <FormLabel>Search</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search message, subject..."
                            startDecorator={<SearchIcon />}
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Project</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Project name..."
                            value={projectNameFilter}
                            onChange={(e) => setProjectNameFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Status</FormLabel>
                        <Select size="sm" placeholder="All statuses" value={statusFilter} onChange={(_, val) => setStatusFilter(val || '')}>
                            <Option value="">All</Option>
                            <Option value="sent">Sent</Option>
                            <Option value="pending">Pending</Option>
                            <Option value="failed">Failed</Option>
                            <Option value="skipped">Skipped</Option>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Type</FormLabel>
                        <Select size="sm" placeholder="All types" value={typeFilter} onChange={(_, val) => setTypeFilter(val || '')}>
                            <Option value="">All</Option>
                            <Option value="arrival">Arrival</Option>
                            <Option value="departure">Departure</Option>
                            <Option value="delay">Delay</Option>
                            <Option value="idle">Idle</Option>
                            <Option value="nearby">Nearby</Option>
                            <Option value="speed-limit">Speed Limit</Option>
                            <Option value="wakeup-call">Wakeup Call</Option>
                            <Option value="missing-paperwork">Missing Paperwork</Option>
                            <Option value="long-loading">Long Loading</Option>
                            <Option value="long-unloading">Long Unloading</Option>
                            <Option value="update-frequency">Update Frequency</Option>
                            <Option value="unknown">Unknown</Option>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Channel</FormLabel>
                        <Select size="sm" placeholder="All channels" value={channelFilter} onChange={(_, val) => setChannelFilter(val || '')}>
                            <Option value="">All</Option>
                            <Option value="email-broker">Email (Broker)</Option>
                            <Option value="telegram-internal-team">Telegram (Internal)</Option>
                            <Option value="telegram-driver">Telegram (Driver)</Option>
                            <Option value="telegram-dispatcher">Telegram (Dispatcher)</Option>
                            <Option value="telegram-custom-group">Telegram (Custom Group)</Option>
                            <Option value="telegram-load-group">Telegram (Load Group)</Option>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Load ID</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Load ID..."
                            value={loadIdFilter}
                            onChange={(e) => setLoadIdFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>From</FormLabel>
                        <Input size="sm" type="date" value={dateFromFilter} onChange={(e) => setDateFromFilter(e.target.value)} />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>To</FormLabel>
                        <Input size="sm" type="date" value={dateToFilter} onChange={(e) => setDateToFilter(e.target.value)} />
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end' }}>
                        <Button size="sm" variant="solid" color="primary" onClick={applyFilters}>
                            Apply
                        </Button>
                        <Button size="sm" variant="outlined" color="neutral" onClick={clearFilters}>
                            Clear
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Table */}
            <Sheet
                variant="outlined"
                sx={{
                    display: { xs: 'none', sm: 'initial' },
                    width: '100%',
                    borderRadius: 'sm',
                    flexShrink: 1,
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                <Table
                    aria-labelledby="notification-logs-table-title"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '4px',
                        '--TableCell-paddingX': '8px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 50, padding: '12px 6px' }}>Actions</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Status</th>
                            <th style={{ width: 120, padding: '12px 6px' }}>Type</th>
                            <th style={{ width: 100, padding: '12px 6px' }}>Channel</th>
                            <th style={{ width: 300, padding: '12px 6px' }}>Message</th>
                            <th style={{ width: 180, padding: '12px 6px' }}>Recipient</th>
                            <th style={{ width: 170, padding: '12px 6px' }}>Load ID</th>

                            <th style={{ width: 200, padding: '12px 6px' }}>Subject</th>

                            <th style={{ width: 120, padding: '12px 6px' }}>Project</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => {
                            // Extract meta for this row
                            let metaObj: any = {}
                            if (row.meta) {
                                try {
                                    metaObj = typeof row.meta === 'string' ? JSON.parse(row.meta) : row.meta
                                } catch (error) {
                                    metaObj = {}
                                }
                            }

                            return (
                                <tr key={row._id || index}>
                                    <td>
                                        <Tooltip title="View Details">
                                            <IconButton size="sm" variant="plain" onClick={() => openDetailModal(row)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="soft" color={statusColors[row.status || ''] || 'neutral'}>
                                            {row.status || 'Unknown'}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Chip size="sm" variant="soft" color={typeColors[row.notificationType?.toLowerCase()] || 'neutral'}>
                                            {row.notificationType || 'N/A'}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.channel || 'N/A'}</Typography>
                                    </td>
                                    <td>
                                        <Tooltip title={row.message}>
                                            <Typography level="body-xs" sx={{ cursor: 'default' }}>
                                                {truncateMessage(row.message)}
                                            </Typography>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.recipientEmail || 'N/A'}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.loadId || 'N/A'}</Typography>
                                    </td>

                                    <td>
                                        <Tooltip title={row.subject}>
                                            <Typography level="body-xs" sx={{ cursor: 'default' }}>
                                                {row.subject ? truncateMessage(row.subject, 20) : 'N/A'}
                                            </Typography>
                                        </Tooltip>
                                    </td>

                                    <td>
                                        <Typography level="body-xs">{row.projectName || 'N/A'}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.createdAt ? formatToUserLocalTime(row.createdAt) : 'N/A'}</Typography>
                                    </td>
                                </tr>
                            )
                        })}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={14} style={{ textAlign: 'center', padding: '32px' }}>
                                    <Typography level="body-sm" color="neutral">
                                        No notification logs found
                                    </Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>

            {/* Detail Modal */}
            <Modal open={detailModalOpen} onClose={() => setDetailModalOpen(false)}>
                <ModalDialog sx={{ width: 700, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Notification Log Details
                    </Typography>

                    {selectedLog && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Status & Type Row */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Status
                                    </Typography>
                                    <Chip size="sm" variant="soft" color={statusColors[selectedLog.status || ''] || 'neutral'}>
                                        {selectedLog.status || 'Unknown'}
                                    </Chip>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Type
                                    </Typography>
                                    <Chip size="sm" variant="soft" color={typeColors[selectedLog.notificationType?.toLowerCase()] || 'neutral'}>
                                        {selectedLog.notificationType || 'N/A'}
                                    </Chip>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Channel
                                    </Typography>
                                    <Typography level="body-sm">{selectedLog.channel || 'N/A'}</Typography>
                                </Box>
                            </Box>

                            {/* IDs Row */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Notification ID
                                    </Typography>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                        {selectedLog._id}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Load ID
                                    </Typography>
                                    <Typography level="body-sm">{selectedLog.loadId || 'N/A'}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Project Name
                                    </Typography>
                                    <Typography level="body-sm">{selectedLog.projectName || 'N/A'}</Typography>
                                </Box>
                            </Box>

                            {/* Recipient Info */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Recipient Email
                                    </Typography>
                                    <Typography level="body-sm">{selectedLog.recipientEmail || 'N/A'}</Typography>
                                </Box>
                            </Box>

                            {/* Subject */}
                            {selectedLog.subject && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Subject
                                    </Typography>
                                    <Typography level="body-sm">{selectedLog.subject}</Typography>
                                </Box>
                            )}

                            {/* Message */}
                            <Box>
                                <Typography level="body-sm" fontWeight="bold">
                                    Message
                                </Typography>
                                <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', maxHeight: 200, overflow: 'auto' }}>
                                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedLog.message}
                                    </Typography>
                                </Sheet>
                            </Box>

                            {/* Error Message */}
                            {selectedLog.errorMessage && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold" color="danger">
                                        Error Message
                                    </Typography>
                                    <Sheet variant="soft" color="danger" sx={{ p: 1.5, borderRadius: 'sm', maxHeight: 150, overflow: 'auto' }}>
                                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedLog.errorMessage}
                                        </Typography>
                                    </Sheet>
                                </Box>
                            )}

                            {/* Error Stack */}
                            {selectedLog.errorStack && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold" color="danger">
                                        Error Stack
                                    </Typography>
                                    <Sheet variant="soft" color="danger" sx={{ p: 1.5, borderRadius: 'sm', maxHeight: 200, overflow: 'auto' }}>
                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                                            {selectedLog.errorStack}
                                        </Typography>
                                    </Sheet>
                                </Box>
                            )}

                            {/* Template */}
                            {selectedLog.templateName && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Template
                                    </Typography>
                                    <Typography level="body-sm">{selectedLog.templateName}</Typography>
                                </Box>
                            )}

                            {/* Error Info */}
                            {selectedLog.errorMessage && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold" color="danger">
                                        Error Message
                                    </Typography>
                                    <Sheet variant="soft" color="danger" sx={{ p: 1.5, borderRadius: 'sm' }}>
                                        <Typography level="body-sm">{selectedLog.errorMessage}</Typography>
                                    </Sheet>
                                </Box>
                            )}

                            {selectedLog.errorStack && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold" color="danger">
                                        Error Stack
                                    </Typography>
                                    <Sheet variant="soft" color="danger" sx={{ p: 1.5, borderRadius: 'sm', maxHeight: 150, overflow: 'auto' }}>
                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                            {selectedLog.errorStack}
                                        </Typography>
                                    </Sheet>
                                </Box>
                            )}

                            {/* Timestamps */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Created At
                                    </Typography>
                                    <Typography level="body-sm">{selectedLog.createdAt ? formatToUserLocalTime(selectedLog.createdAt) : 'N/A'}</Typography>
                                </Box>
                            </Box>

                            {/* Meta */}
                            {selectedLog.meta && (
                                <Box>
                                    <Typography level="body-sm" fontWeight="bold">
                                        Additional Meta
                                    </Typography>
                                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', maxHeight: 150, overflow: 'auto' }}>
                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                            {(() => {
                                                try {
                                                    const metaObj = typeof selectedLog.meta === 'string' ? JSON.parse(selectedLog.meta) : selectedLog.meta

                                                    // Filter out fields that are already displayed elsewhere
                                                    const filteredMeta = { ...metaObj }
                                                    const fieldsToRemove = [
                                                        'status',
                                                        'notificationType',
                                                        'loadId',
                                                        'channel',
                                                        'recipientEmail',
                                                        'recipientPhone',
                                                        'subject',
                                                        'errorMessage',
                                                        'errorStack',
                                                        'retryCount',
                                                        'triggeredBy',
                                                        'durationMs',
                                                        'responseCode',
                                                        'deliveryId',
                                                        'truckId',
                                                        'sourceRateConfId',
                                                        'recipientTypes',
                                                        'templateName', // templateName is completely removed
                                                    ]

                                                    fieldsToRemove.forEach((field) => {
                                                        delete filteredMeta[field]
                                                    })

                                                    return Object.keys(filteredMeta).length > 0 ? JSON.stringify(filteredMeta, null, 2) : 'No additional data'
                                                } catch (error) {
                                                    return 'Unable to parse meta data'
                                                }
                                            })()}
                                        </Typography>
                                    </Sheet>
                                </Box>
                            )}
                        </Box>
                    )}
                </ModalDialog>
            </Modal>
        </React.Fragment>
    )
}
